import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import {
	generatePath,
	useHistory,
	useLocation,
	useParams,
} from "react-router-dom";
import routing from "routing";
import { TextField } from "src/components/Default/LekUI/TextField";
import newApi from "src/newApi";
import * as Yup from "yup";

import { getServiceId, isLek } from "../../../../helpers/services";
import { PrimaryButton } from "../../../Default/LekUI/Button";
import { TextWithLink } from "../../../Default/LekUI/Layout/TextWithLink";
import ServiceBox from "./ServiceBox";

function SignInForm(props) {
	const { t } = useTranslation();
	const params = useParams();
	const { state } = useLocation();
	const history = useHistory();

	const redirectToPage = (id) => {
		return history.push({
			pathname: generatePath(routing.paths.signIn, { id }),
			state,
		});
	};

	const {
		handleSubmit,
		handleChange: handleChangeFormik,
		values,
		errors,
		setErrors,
		isSubmitting,
	} = useFormik({
		initialValues: {
			login: "",
			password: "",
		},
		validateOnChange: false,
		onSubmit: async (values, { setFieldError }) => {
			try {
				await newApi.login(values, params.id === "pes");
				location.href = props.redirectUrl || routing.paths.home;
			} catch {
				setFieldError("login", t("errors.wrongPasswordOrLogin"));
			}
		},
		validationSchema: Yup.object().shape({
			login: Yup.string().required().typeError(t("errors.emptyUserName")),
			password: Yup.string().min(8, t("errors.passwordToShort")).required(),
		}),
	});

	const handleChange = (...params) => {
		setErrors({});
		handleChangeFormik(...params);
	};

	if (params.id && !["lek", "pes", "ldek", "sistesty"].includes(params.id)) {
		return redirectToPage();
	}

	const loginStep = () => (
		<>
			<div className="flex flex-col w-100 gap-24">
				<TextField
					className="w-100"
					required
					id="login"
					label={t("auth.form.login")}
					name="login"
					type="text"
					onChange={handleChange}
					autoComplete="login"
					value={values.login}
					error={errors.login}
					slotProps={{ htmlInput: { "data-test-id": "login-email-input" } }}
				/>
				<TextField
					className="w-100"
					required
					name="password"
					label={t("auth.form.password")}
					type="password"
					id="password"
					autoComplete="current-password"
					onChange={handleChange}
					value={values.password}
					error={errors.password}
					slotProps={{ htmlInput: { "data-test-id": "login-password-input" } }}
				/>
			</div>
			<div className="flex flex-col gap-24 w-100">
				<TextWithLink
					className="align-self-end mt-8"
					link={routing.paths.resetPassword}
					linkText={t("auth.forgotPassword")}
				/>
				<PrimaryButton
					type="submit"
					disabled={isSubmitting}
					data-test-id="login-submit-button"
				>
					{t(isSubmitting ? "auth.signingIn" : "auth.signIn")}
				</PrimaryButton>
			</div>
		</>
	);

	const renderSteps = () => {
		if (!params.id && !isLek()) {
			return redirectToPage(getServiceId());
		}

		if (!params.id) {
			return <ServiceBox onChange={(service) => redirectToPage(service)} />;
		}

		return loginStep();
	};

	return (
		<>
			<form onSubmit={handleSubmit} className="auth--form">
				{renderSteps()}
			</form>
			<TextWithLink
				link={routing.paths.signUp}
				linkText={t("auth.signUp")}
				questionText={t("auth.signUpQuestion")}
			/>
		</>
	);
}

export default SignInForm;
